/**
 * TT-803 Phase 3 — the only place the Commerce Control Room talks to
 * `/api/commerce-control`.
 *
 * Seven calls, matching `commerce_control_routes.py` exactly: read the state,
 * read the bounded category/product options, take/renew the whole-room editor
 * lease, give it back, preview a change, apply it, read recent activity. No
 * other module in the frontend issues a request to this prefix, so the audited
 * write path has one door.
 *
 * Errors are normalized once, here, into `CommerceControlError`. The routes
 * answer a failure with `detail = {code, message}` plus `current` on a version
 * conflict or `holder` on a lease conflict, and *four* distinct failures share
 * HTTP 409: a stale row version, a missing confirmation phrase, a room held by
 * somebody else, and a room nobody holds. Callers must be able to tell them
 * apart without reading prose — "someone else is editing" is not something a
 * retry fixes, while "take the room first" is — so `code` is carried through
 * verbatim rather than collapsed into a status check.
 *
 * HTTP 403 is ambiguous in the same way and is separated the same way. Three
 * different refusals arrive under it — the capability was never granted (a
 * plain string from `commerce_control_auth`), the room is switched off (also a
 * plain string), and the room is on but its activation gates are failing (a
 * shaped `{code: "control_room_not_ready", blocking_gates, blockers}`). Only
 * the third names something an operator can go and fix, so its gate list is
 * carried through verbatim instead of being flattened into "denied".
 */
import api from "@/lib/api";
import { MAX_PRODUCT_LIMIT, MAX_SEARCH_LENGTH } from "./commerceControlOptions";

export const COMMERCE_CONTROL_BASE = "/commerce-control";

export const CODE_VERSION_CONFLICT = "version_conflict";
export const CODE_CONFIRMATION_REQUIRED = "confirmation_required";
// Mirrors commerce_editor_lease.CODE_HELD_BY_OTHER / CODE_NOT_HELD.
export const CODE_LEASE_HELD_BY_OTHER = "editor_lease_held_by_other";
export const CODE_LEASE_REQUIRED = "editor_lease_required";
// Mirrors commerce_control_auth.NOT_READY_CODE.
export const CODE_NOT_READY = "control_room_not_ready";

export class CommerceControlError extends Error {
  constructor({ status, code, message, current, holder, blockingGates, blockers }) {
    super(message || "Something went wrong");
    this.name = "CommerceControlError";
    this.status = status || 0;
    this.code = code || "";
    this.current = current ?? null;
    // Peer-safe description of whoever holds the room, straight from the
    // server. It never carries a phone number (see
    // `commerce_editor_lease.holder_view`), and nothing here adds one.
    this.holder = holder ?? null;
    // The activation gates a refused write named, verbatim. Empty for every
    // other failure — a caller must not have to tell "no gate was reported"
    // apart from "every gate passed" by reading the message.
    this.blockingGates = Array.isArray(blockingGates) ? blockingGates : [];
    this.blockers = Array.isArray(blockers) ? blockers : [];
  }

  get isVersionConflict() {
    return this.code === CODE_VERSION_CONFLICT;
  }

  get isConfirmationRequired() {
    return this.code === CODE_CONFIRMATION_REQUIRED;
  }

  /** Somebody else is editing. Waiting helps; retrying does not. */
  get isLeaseHeldByOther() {
    return this.code === CODE_LEASE_HELD_BY_OTHER;
  }

  /** Nobody holds the room, including this caller — take it and try again. */
  get isLeaseRequired() {
    return this.code === CODE_LEASE_REQUIRED;
  }

  /** Either lease failure: whatever this screen thought it held, it does not. */
  get isLeaseProblem() {
    return this.isLeaseHeldByOther || this.isLeaseRequired;
  }

  get isDenied() {
    return this.status === 403;
  }

  /**
   * The room is on but not built — the write was refused by an activation gate.
   *
   * Distinct from every other 403 because it is the only one that is *not*
   * about this operator: the capability is granted, the flag is on, and the
   * database drifted underneath a live room. `blockers` names what to fix.
   */
  get isNotReady() {
    return this.code === CODE_NOT_READY;
  }

  /** The gate sentences to show, or `[]` when the refusal named none. */
  get readinessBlockerMessages() {
    return this.blockers
      .map((blocker) => (typeof blocker?.message === "string" ? blocker.message : ""))
      .filter(Boolean);
  }
}

function normalizeError(ex) {
  if (ex instanceof CommerceControlError) return ex;
  const status = Number(ex?.response?.status) || 0;
  const detail = ex?.response?.data?.detail;
  let code = "";
  let message = "";
  let current = null;
  let holder = null;
  let blockingGates = [];
  let blockers = [];
  if (detail && typeof detail === "object") {
    code = typeof detail.code === "string" ? detail.code : "";
    message = typeof detail.message === "string" ? detail.message : "";
    current = detail.current ?? null;
    holder = detail.holder ?? null;
    // Only the readiness refusal carries these. Read defensively rather than
    // keyed off `code`, so a future refusal that also names its gates is not
    // silently stripped of them by this build.
    blockingGates = Array.isArray(detail.blocking_gates)
      ? detail.blocking_gates.filter((gate) => typeof gate === "string" && gate)
      : [];
    blockers = Array.isArray(detail.blockers)
      ? detail.blockers.filter((blocker) => blocker && typeof blocker === "object")
      : [];
  } else if (typeof detail === "string") {
    message = detail;
  }
  if (!message) {
    message = ex?.message || "Something went wrong";
  }
  return new CommerceControlError({
    status,
    code,
    message,
    current,
    holder,
    blockingGates,
    blockers,
  });
}

async function call(run) {
  try {
    const res = await run();
    return res?.data ?? {};
  } catch (ex) {
    throw normalizeError(ex);
  }
}

/** Every surface's rows and versions, the control-room flag, the current
 *  whole-room `editor_lease`, and the field declarations the editor renders
 *  from. This is the only source of who holds the room — the UI never decides
 *  that for itself. */
export function fetchCommerceControlState() {
  return call(() => api.get(`${COMMERCE_CONTROL_BASE}/state`));
}

/**
 * Take the Commerce Control Room, or renew a lease already held.
 *
 * Acquire and renew are deliberately the same call, exactly as the route
 * defines them: a holder who has been filling in a form for a while is never
 * told they lost the room merely because time passed. The response carries the
 * server's `lease` block — `{lease, renewed, took_over}` — and callers must
 * key off `lease.can_edit` rather than off the absence of an error.
 *
 * There is no holder field to send. Who is taking the room is resolved from
 * the caller's DB-backed identity server-side, so a client cannot claim to be
 * anyone.
 */
export function acquireCommerceControlLease({ requestId } = {}) {
  const body = {};
  if (requestId) body.request_id = requestId;
  return call(() => api.post(`${COMMERCE_CONTROL_BASE}/lease`, body));
}

/**
 * Give the room back so the next operator does not have to wait out the TTL.
 *
 * Best effort by nature: a browser that is being torn down may never deliver
 * it. That is survivable and is not a hole — the lease expires on its own
 * after `ttl_seconds`, which is the actual guarantee that the room frees up.
 */
export function releaseCommerceControlLease({ requestId } = {}) {
  const body = {};
  if (requestId) body.request_id = requestId;
  return call(() => api.post(`${COMMERCE_CONTROL_BASE}/lease/release`, body));
}

/**
 * The bounded pick-lists a category/product control may legally name.
 *
 * A read, and deliberately not gated on the room being switched on or ready:
 * an operator diagnosing an incident must be able to see which categories and
 * products exist while the room is still being built. Mirrors
 * `GET /commerce-control/options` exactly — `search` is normalized and
 * `limit` is capped server-side (`commerce_control_options`), so this function
 * sends what the operator typed and renders what comes back rather than
 * pre-filtering against a second copy of those bounds.
 *
 * Omitting `search` is how "no filter" is expressed; an empty string is not
 * sent, because the route's `Query(default=None)` and an empty term mean the
 * same thing and sending one would only vary the URL.
 *
 * The term is truncated to `MAX_SEARCH_LENGTH` and the limit to
 * `MAX_PRODUCT_LIMIT` — the *route's own* declared bounds, not a second opinion
 * about them. Exceeding either is answered 422 by FastAPI before the handler
 * runs, and "the request was malformed" is not a useful thing to render into a
 * picker; truncating asks the question the operator meant. Everything past
 * that — how the term is matched, how the page is capped, what `truncated`
 * means — stays entirely server-side.
 */
export function fetchCommerceControlOptions({ search, limit } = {}) {
  const params = {};
  const term = typeof search === "string" ? search.trim().slice(0, MAX_SEARCH_LENGTH) : "";
  if (term) params.search = term;
  if (Number.isFinite(limit) && limit > 0) {
    params.limit = Math.min(Math.trunc(limit), MAX_PRODUCT_LIMIT);
  }
  return call(() => api.get(`${COMMERCE_CONTROL_BASE}/options`, { params }));
}

/** Validate without writing. Returns the confirmation phrase (when the surface
 *  is dangerous), the expected version to submit, and the changed fields. */
export function previewCommerceControlChange(surface, { action = "set", payload }) {
  return call(() =>
    api.post(`${COMMERCE_CONTROL_BASE}/${encodeURIComponent(surface)}/preview`, {
      action,
      payload,
    })
  );
}

/**
 * Author one change.
 *
 * `expectedVersion` is required by the server and deliberately not defaulted
 * here: a caller that cannot state the version it read must not be able to
 * write, because that absence is exactly what turns a second editor's stale
 * form into a 409 instead of a silent overwrite.
 */
export function applyCommerceControlChange(
  surface,
  { action = "set", payload, expectedVersion, reason, confirm, requestId }
) {
  const body = {
    action,
    payload,
    expected_version: expectedVersion,
    reason,
  };
  if (confirm) body.confirm = confirm;
  if (requestId) body.request_id = requestId;
  return call(() => api.post(`${COMMERCE_CONTROL_BASE}/${encodeURIComponent(surface)}`, body));
}

export function fetchCommerceControlAudit(limit = 25) {
  return call(() => api.get(`${COMMERCE_CONTROL_BASE}/audit`, { params: { limit } }));
}

/**
 * Is this operator allowed into the control room at all?
 *
 * Used by the Settings tab strip so the entry point is hidden rather than
 * offered and then denied. The capability is a per-person grant whose column
 * is not in production yet, so today this resolves to `false` for everyone and
 * no tab appears — which is the intended disabled default, not a bug.
 *
 * Cached for the life of the SPA session: the answer is a role grant, it does
 * not change while a page is open, and re-asking on every Settings sub-route
 * render would issue a request per navigation for a value that is nearly
 * always "no".
 */
let accessProbe = null;

export function probeCommerceControlAccess() {
  if (!accessProbe) {
    accessProbe = fetchCommerceControlState()
      .then(() => ({ allowed: true, status: 200 }))
      .catch((ex) => ({ allowed: false, status: ex?.status || 0 }));
  }
  return accessProbe;
}

/** Test seam and post-login reset — drops the cached probe. */
export function resetCommerceControlAccessProbe() {
  accessProbe = null;
}
